import { ReactElement, useCallback } from 'react';
import { DialogTitle } from '@mui/material';
import { FormikHelpers, FormikProvider, useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import { pick } from 'lodash';
import moment from 'moment-timezone';

import { createScheduleRoute } from '~services/scheduleRoute';
import { createRouteByTemplate } from '~services/route';
import { useUtcOffset } from '~hooks/useUtcOffset';
import { useLazyRequest } from '~hooks/useLazyRequest';

import {
  SCHEDULE_ROUTE_BASE_FORM_FIELDS_NAME,
  getScheduleRouteBody,
} from '../../utils/scheduleRouteForm';

import { useAssistantGenerateRoutesDialogByTemplateContext } from '../Context';
import {
  FORM_BY_TEMPLATE_FIELDS_NAME,
  getFormByTemplateInitialValues,
  validationFormByTemplateSchema,
  getVehicleBySelectedTemplate,
} from '../utils';

import AssistantGenerateRoutesDialogByTemplateContentTabs from './Tabs';
import AssistantGenerateRoutesDialogByTemplateContentActions from './Actions';

const AssistantGenerateRoutesDialogByTemplateContent = (): ReactElement => {
  const { enqueueSnackbar } = useSnackbar();

  const formatterDateUtcOffset = useUtcOffset();

  const {
    activeTab: { setActiveTab },
    data: {
      selectedTemplate: { value: selectedTemplate },
      selectedDriver: { value: selectedDriver },
    },
    stepper: {
      currentApiTransactionId: { setCurrentApiTransactionId },
    },
    actions,
  } = useAssistantGenerateRoutesDialogByTemplateContext();

  const [, , , executeCreateScheduleRoute] = useLazyRequest({
    request: createScheduleRoute,
    withPostSuccess: (response) => {
      const apiTransactionId = response.data?.data.apiTransactionId as string;

      actions?.onGenerateRouteComplete?.(apiTransactionId);

      setCurrentApiTransactionId(apiTransactionId);

      setActiveTab('stepper');
    },
    withPostFailure: (err) => {
      let errorMessage = 'Ha ocurrido un error, intente nuevamente';

      if (err?.data?.data.message) {
        errorMessage = err.data.data.message;
      }

      enqueueSnackbar(errorMessage, { variant: 'error' });
    },
  });

  const handleCreateRouteSchedule = useCallback(
    async (formValues: ReturnType<typeof getFormByTemplateInitialValues>) => {
      const scheduleRouteTitle =
        formValues[FORM_BY_TEMPLATE_FIELDS_NAME.SCHEDULE_NAME];
      const scheduleRouteStartDate = formatterDateUtcOffset(
        moment(
          formValues[FORM_BY_TEMPLATE_FIELDS_NAME.SCHEDULE_START_DATE],
        ).startOf('day'),
        moment.HTML5_FMT.DATETIME_LOCAL_MS,
        true,
      );
      const scheduleRouteVehicle =
        getVehicleBySelectedTemplate(selectedTemplate);
      const scheduleRouteFormValues = pick(
        formValues,
        Object.values(SCHEDULE_ROUTE_BASE_FORM_FIELDS_NAME),
      );

      const body = getScheduleRouteBody({
        title: scheduleRouteTitle,
        startDate: scheduleRouteStartDate,
        vehicleId: scheduleRouteVehicle?.vehicleId ?? '',
        driverId: selectedDriver?.id ?? null,
        templateId: selectedTemplate?.routeId ?? null,
        formValues: scheduleRouteFormValues,
        formatterDateUtcOffset,
      });

      await executeCreateScheduleRoute(body);
    },
    [
      selectedTemplate,
      selectedDriver,
      formatterDateUtcOffset,
      executeCreateScheduleRoute,
    ],
  );

  const [, , , executeCreateRouteByTemplate] = useLazyRequest({
    request: createRouteByTemplate,
    withPostSuccess: (response) => {
      const apiTransactionId = response.data?.data.apiTransactionId as string;

      actions?.onGenerateRouteComplete?.(apiTransactionId);

      setCurrentApiTransactionId(apiTransactionId);

      setActiveTab('stepper');
    },
    withPostFailure: (err) => {
      let errorMessage = 'Ha ocurrido un error, intente nuevamente';

      if (err?.data?.data.message) {
        errorMessage = err.data.data.message;
      }

      enqueueSnackbar(errorMessage, { variant: 'error' });
    },
  });

  const handleCreateRouteByTemplate = useCallback(
    async (formValues: ReturnType<typeof getFormByTemplateInitialValues>) => {
      const currentStartDate = formatterDateUtcOffset(
        moment(
          formValues[FORM_BY_TEMPLATE_FIELDS_NAME.SCHEDULE_START_DATE],
        ).startOf('day'),
        moment.HTML5_FMT.DATETIME_LOCAL_MS,
        true,
      );

      const name = formValues[FORM_BY_TEMPLATE_FIELDS_NAME.SCHEDULE_NAME];

      await executeCreateRouteByTemplate({
        routeTemplateId: selectedTemplate?.routeId ?? '',
        date: currentStartDate,
        name,
        driverId: selectedDriver?.id ?? null,
      });
    },
    [
      selectedTemplate,
      selectedDriver,
      formatterDateUtcOffset,
      executeCreateRouteByTemplate,
    ],
  );

  const handleSubmit = useCallback(
    async (
      formValues: ReturnType<typeof getFormByTemplateInitialValues>,
      {
        setSubmitting,
      }: FormikHelpers<ReturnType<typeof getFormByTemplateInitialValues>>,
    ) => {
      const isScheduleRouteEnabled =
        formValues[SCHEDULE_ROUTE_BASE_FORM_FIELDS_NAME.SCHEDULE_ROUTE_ENABLED];

      if (isScheduleRouteEnabled) {
        await handleCreateRouteSchedule(formValues);

        return;
      }

      await handleCreateRouteByTemplate(formValues);

      setSubmitting(false);
    },
    [handleCreateRouteSchedule, handleCreateRouteByTemplate],
  );

  const formikBag = useFormik({
    initialValues: getFormByTemplateInitialValues(
      selectedTemplate?.apiTransaction.name,
    ),
    validationSchema: validationFormByTemplateSchema,
    onSubmit: handleSubmit,
  });

  return (
    <>
      <FormikProvider value={formikBag}>
        <DialogTitle>Generar Ruta</DialogTitle>

        <AssistantGenerateRoutesDialogByTemplateContentTabs />

        <AssistantGenerateRoutesDialogByTemplateContentActions />
      </FormikProvider>
    </>
  );
};

export default AssistantGenerateRoutesDialogByTemplateContent;
